import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, Sparkles, MessageSquare, Phone, Receipt, LayoutPanelLeft } from "lucide-react";
import { DISPLAY, Glass, WinBar, useLawfirmFonts } from "./ui";

const CHIPS = [[Briefcase, "Matters"], [Sparkles, "Live Copilot"], [MessageSquare, "Client messages"], [Phone, "AI phone"], [Receipt, "Billing"], [LayoutPanelLeft, "Client portal"]];

export function LawfirmAuthBackdrop() {
  useLawfirmFonts();
  return (
    <>
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(55% 55% at 30% 15%, rgba(59,130,246,0.22), transparent 70%), radial-gradient(40% 45% at 90% 85%, rgba(139,92,246,0.18), transparent 70%)" }} />
      <div aria-hidden className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)", backgroundSize: "64px 64px" }} />
    </>
  );
}

export function LawfirmTopBar({ absolute = true }) {
  useLawfirmFonts();
  return (
    <div className={`${absolute ? "absolute top-0 inset-x-0" : "relative"} z-30`} style={{ paddingTop: "env(safe-area-inset-top)" }} data-testid="lf-topbar">
      <div className="max-w-7xl mx-auto h-16 px-5 sm:px-8 flex items-center">
        <Link to="/lawfirm" style={DISPLAY} className="text-[17px] font-semibold tracking-[0.32em] text-white" data-testid="brand-logo">LEXARRA</Link>
      </div>
    </div>
  );
}

export function LawfirmAuthHero({ mode }) {
  const signup = mode !== "login";
  return (
    <div className="hidden lg:flex relative col-span-7 flex-col justify-between p-14 xl:p-20 overflow-hidden" data-testid="lf-auth-hero">
      <div className="h-6" aria-hidden />
      <div className="max-w-xl">
        <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} style={DISPLAY} className="text-5xl xl:text-6xl font-semibold leading-[1.02] tracking-[-0.045em]">
          {signup ? <>Build your firm.<br /><span className="bg-gradient-to-r from-cyan-200 via-sky-400 to-violet-400 bg-clip-text text-transparent">Run it on Lexarra.</span></> : <>Your firm.<br /><span className="bg-gradient-to-r from-cyan-200 via-sky-400 to-violet-400 bg-clip-text text-transparent">Welcome back.</span></>}
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.7 }} style={DISPLAY} className="mt-6 text-xl text-white/85 tracking-tight">One platform. Your entire practice. AI at every step.</motion.p>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }} className="mt-10 relative">
          <div aria-hidden className="absolute -inset-8 rounded-[40px] bg-sky-500/15 blur-3xl pointer-events-none" />
          <Glass className="relative overflow-hidden bg-[#0b0d14]/85">
            <WinBar title="Lexarra · Your firm workspace" right={<span className="text-cyan-300/80">● Live</span>} />
            <div className="p-4 grid grid-cols-3 gap-2.5">
              {CHIPS.map(([I, t], i) => (
                <motion.div key={t} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 + i * 0.07 }} className="rounded-xl border border-white/[0.07] bg-white/[0.03] px-3 py-3">
                  <I size={15} className="text-cyan-300" /><div className="mt-2 text-[12px] text-white/75">{t}</div>
                </motion.div>
              ))}
            </div>
          </Glass>
        </motion.div>
      </div>
      <div className="flex items-center gap-3 text-[12px] text-white/45 tracking-wide">
        {["Your clients", "Your matters", "Your brand"].map((t, i) => <React.Fragment key={t}>{i > 0 && <span className="text-white/25">·</span>}<span>{t}</span></React.Fragment>)}
      </div>
    </div>
  );
}
